import { useEffect, useState } from 'react'
import {
    SubmitHandler,
    UseFormReset,
    UseFormSetValue,
    useForm
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CardForm } from './CardForm'
import { GuardianSection, guardianFields } from './GuardianSection'

import { IsAgentComponent } from '@/components/common/'
import { FormNavigation } from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import { useAuthHook, useDocumentTypes } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { Authorization, AuthorizationType } from '@/types/authorizationForm'
import { AuthorizationFormData, FormsGroupsProps } from '@/types/forms/'
import { useFormProgress } from '@/utils'

type JobAuthFormData = AuthorizationFormData & GuardianFields

type GuardianFields = {
    isMinor?: boolean
    guardianName?: string
    guardianDocumentType?: string
    guardianDocumentNumber?: string
    guardianMainEconomicActivity?: string
    guardianSecondaryEconomicActivity?: string
    guardianSectionTouched?: boolean
}

const applyBasicData = (
    response: any,
    setValue: UseFormSetValue<JobAuthFormData>
): void => {
    if (response.status !== 200) {
        return
    }

    setValue('isMinor', response.data.isMinor ?? false)
}

const buildAuthorizationFormData = (
    authData: Authorization[]
): Partial<AuthorizationFormData> => ({
    economicActivity:
        authData.find(
            (auth) =>
                auth.authorizationType === AuthorizationType.ECONOMIC_ACTIVITY
        )?.valueText || '',
    secondEconomicActivity:
        authData.find(
            (auth) =>
                auth.authorizationType ===
                AuthorizationType.ECONOMIC_ACTIVITY_SECONDARY
        )?.valueText || ''
})

const applyAuthorizationData = (
    response: any,
    reset: UseFormReset<JobAuthFormData>,
    isAgent: boolean,
    setDisable: (v: boolean) => void
): void => {
    if (response.status !== 200) {
        return
    }

    const formData = buildAuthorizationFormData(response.data)

    if (
        formData.economicActivity ||
        formData.secondEconomicActivity ||
        isAgent
    ) {
        setDisable(true)
    }

    reset(formData)
}

const applyFinancialSponsor = (
    response: any,
    reset: UseFormReset<JobAuthFormData>
): void => {
    if (response.status !== 200) {
        return
    }

    const sponsor = response.data

    reset((prev) => ({
        ...prev,
        guardianName: sponsor?.nombre ?? '',
        guardianDocumentType: sponsor?.tipoDocumento ?? '',
        guardianDocumentNumber: sponsor?.numeroDocumentoIdentidad ?? '',
        guardianMainEconomicActivity:
            sponsor?.actividadEconomicaPrincipal ?? '',
        guardianSecondaryEconomicActivity:
            sponsor?.actividadEconomicaSecundaria ?? ''
    }))
}

const buildAuthorizationsPayload = (
    data: JobAuthFormData
): {
    authorizationTypeId: number
    isAccepted: boolean
    valueText: string
}[] => [
    {
        authorizationTypeId: AuthorizationType.ECONOMIC_ACTIVITY,
        isAccepted: true,
        valueText: data.economicActivity || ''
    },
    ...(data.secondEconomicActivity
        ? [
              {
                  authorizationTypeId:
                      AuthorizationType.ECONOMIC_ACTIVITY_SECONDARY,
                  isAccepted: true,
                  valueText: data.secondEconomicActivity
              }
          ]
        : [])
]

// eslint-disable-next-line max-lines-per-function
export const JobAuthorization = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): React.ReactElement => {
    const { t } = useTranslation()
    const { isAgent, isMinor: isMinorFromStore } = useAuthStore()
    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        register,
        clearErrors
    } = useForm<JobAuthFormData>({
        mode: 'onChange',
        defaultValues: {
            isMinor: isMinorFromStore ?? false
        }
    })
    const { idAspirant, idInscription } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const {
        documentTypes,
        isLoading: isLoadingDocumentTypes,
        hasError: hasDocumentTypesError
    } = useDocumentTypes()
    const { showToast } = useToast()
    const [disable, setDisable] = useState<boolean>(false)
    const isMinor = watch('isMinor')

    useEffect(() => {
        console.log('isMinor:', isMinor)
    }, [isMinor])

    useEffect(() => {
        if (isMinorFromStore !== null && isMinorFromStore !== undefined) {
            setValue('isMinor', isMinorFromStore)
        }
    }, [isMinorFromStore, setValue])

    // Field management is now handled in GuardianSection component
    const watchedGuardianFields = watch(guardianFields)
    const isGuardianSectionTouched = watchedGuardianFields.some(Boolean)

    useEffect(() => {
        setValue('guardianSectionTouched', isGuardianSectionTouched)
    }, [isGuardianSectionTouched, setValue])

    const progress = useFormProgress<AuthorizationFormData & GuardianFields>({
        watch,
        excludedFields: [
            'aspirantId',
            'acceptEmailNotifications',
            'acceptDataProcessing',
            'acceptProgramTerms',
            'acceptTerms',
            'isMinor',
            'acceptSensitiveData',
            'secondEconomicActivity',
            'guardianSecondaryEconomicActivity',
            'guardianSectionTouched',
            ...(!isGuardianSectionTouched ? guardianFields : [])
        ],
        conditionalFields: [
            {
                field: 'guardianName',
                dependsOn: 'guardianSectionTouched',
                condition: true
            },
            {
                field: 'guardianDocumentType',
                dependsOn: 'guardianSectionTouched',
                condition: true
            },
            {
                field: 'guardianDocumentNumber',
                dependsOn: 'guardianSectionTouched',
                condition: true
            },
            {
                field: 'guardianMainEconomicActivity',
                dependsOn: 'guardianSectionTouched',
                condition: true
            }
        ]
    })

    useEffect(() => {
        const loadAuthorizationData = async (): Promise<void> => {
            if (!idAspirant) {
                return
            }

            const [
                responseAuthByInscription,
                basicDataResponse,
                financialSponsorResponse
            ] = await Promise.all([
                aspirantsApi.getAuthorizationAspirantScope(idAspirant),
                aspirantsApi.getBasicData(idAspirant),
                idInscription
                    ? aspirantsApi.getFinancialSponsorSilent(idInscription)
                    : Promise.resolve({ status: 204, data: null })
            ])

            applyBasicData(basicDataResponse, setValue)
            applyAuthorizationData(
                responseAuthByInscription,
                reset,
                isAgent,
                setDisable
            )
            applyFinancialSponsor(financialSponsorResponse, reset)
        }

        loadAuthorizationData()
    }, [idAspirant, reset, setValue])

    const onSubmit: SubmitHandler<JobAuthFormData> = async (data) => {
        if (data.guardianSectionTouched && idInscription) {
            await aspirantsApi.createFinancialSponsor({
                inscriptionId: idInscription,
                actividadEconomicaPrincipal:
                    data.guardianMainEconomicActivity || '',
                actividadEconomicaSecundaria:
                    data.guardianSecondaryEconomicActivity || '',
                nombre: data.guardianName || '',
                tipoDocumento: data.guardianDocumentType || '',
                numeroDocumentoIdentidad: data.guardianDocumentNumber || ''
            })
        }

        const response = await aspirantsApi.postAuthorizationAspiranByScope({
            idAspirant: idAspirant!,
            updatedBy: idAspirant!,
            authorizations: buildAuthorizationsPayload(data)
        })

        const isSuccess =
            response.status === 200 ||
            response.message?.some((msg: any) =>
                msg.messageDesc.includes('ya existe')
            )

        if (isSuccess) {
            showToast({
                title: t('authorization.title'),
                message: t('forms.ui.save'),
                type: 'success'
            })

            onNext()
        }
    }

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <IsAgentComponent isAgent={isAgent}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="w-full"
                >
                    <input
                        type="hidden"
                        {...register('isMinor')}
                    />
                    <input
                        type="hidden"
                        {...register('guardianSectionTouched')}
                    />
                    <CardForm
                        nameControl="economicActivity"
                        nameControl2="secondEconomicActivity"
                        title={t('authorization.originOfFunds.title')}
                        description={t(
                            'authorization.originOfFunds.description'
                        )}
                        article={[
                            {
                                text: [
                                    t('authorization.originOfFunds.text'),
                                    t('authorization.originOfFunds.text2'),
                                    t('authorization.originOfFunds.text3')
                                ]
                            }
                        ]}
                        inputCategory={2}
                        acceptTerms={[
                            t('authorization.originOfFunds.acceptTerms'),
                            t('authorization.originOfFunds.acceptTerms2')
                        ]}
                        required={t('authorization.originOfFunds.required')}
                        control={control}
                        isDisabled={disable}
                    />

                    <GuardianSection
                        control={control}
                        watch={watch}
                        clearErrors={clearErrors}
                        isDisabled={disable}
                    />

                    <FormNavigation
                        onSubmit={handleSubmit(onSubmit)}
                        onPrevious={onPrevious}
                        progress={progress}
                        submitLabel={t('forms.ui.nextPage')}
                        submitDisabled={isAgent}
                    />
                </form>
            </IsAgentComponent>
        </div>
    )
}
